import { alpha, Avatar, Stack } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { _getAdminViewsFunds } from '@/api/admin/queries';
import useDebounce from '@/global-hooks/use-debounce';
import {
  Badge,
  type BadgeColor,
  Button,
  Input,
  Radio,
  RadioGroup,
  theme,
  Typography,
} from '@/shared/design-system';
import { getInitials } from '@/utils/get-initials';

import { StepProgress } from './step-progress';
import { FooterButtons, FundRadioItem, StepContainer } from './styled';
import type { SelectedFund } from './types';

const STATUS_BADGE_COLORS: Record<string, BadgeColor> = {
  Open: 'primary',
  Private: 'purple',
  PreLaunch: 'yellow',
  Closed: 'default',
};

interface StepSelectFundProps {
  selectedFund: SelectedFund | null;
  onSelect: (_fund: SelectedFund) => void;
  onNext: () => void;
  onBack: () => void;
}

export const StepSelectFund = ({ selectedFund, onSelect, onNext, onBack }: StepSelectFundProps) => {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);

  const { data, isPending } = useQuery({
    ..._getAdminViewsFunds({
      search: debouncedSearch || undefined,
      limit: 20,
      status: ['Verified'],
    }),
  });

  const funds = data?.items ?? [];

  const handleSelect = (fund: (typeof funds)[0]) => {
    onSelect({
      id: fund.id,
      name: fund.name,
      tvl: fund.tvl,
      investmentAvailabilityStatus: fund.investmentAvailabilityStatus,
      thumbnailUrl: fund.thumbnailUrl,
    });
  };

  return (
    <StepContainer>
      <StepProgress currentStep={3} />

      <Input
        placeholder="Search by fund name"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <RadioGroup
        value={selectedFund?.id ?? ''}
        onValueChange={(val) => {
          const fund = funds.find((f) => f.id === val);
          if (fund) handleSelect(fund);
        }}
      >
        <Stack gap="8px" flexDirection="column" sx={{ maxHeight: '320px', overflowY: 'auto' }}>
          {isPending ? (
            <Stack
              alignItems="center"
              justifyContent="center"
              padding="24px"
              borderRadius="10px"
              bgcolor={theme.colors.background.canvas}
            >
              <Typography variant="body2" color={alpha(theme.colors.text.primary, 0.6)}>
                Loading funds...
              </Typography>
            </Stack>
          ) : funds.length === 0 ? (
            <Stack
              borderRadius="10px"
              alignItems="center"
              justifyContent="center"
              padding="24px"
              bgcolor={theme.colors.background.canvas}
            >
              <Typography variant="body2" color={alpha(theme.colors.text.primary, 0.6)}>
                No funds found
              </Typography>
            </Stack>
          ) : (
            funds.map((fund) => {
              const isSelected = selectedFund?.id === fund.id;
              const statusLabel = fund.investmentAvailabilityStatus ?? '';
              const statusColor = STATUS_BADGE_COLORS[statusLabel] ?? 'default';

              return (
                <FundRadioItem
                  key={fund.id}
                  selected={isSelected}
                  onClick={() => handleSelect(fund)}
                >
                  <Stack flexDirection="row" alignItems="center" gap="8px">
                    <Radio value={fund.id} />
                    {fund.thumbnailUrl ? (
                      <Avatar
                        src={fund.thumbnailUrl}
                        sx={{ width: 24, height: 24, borderRadius: '8px' }}
                      />
                    ) : (
                      <Avatar
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: '8px',
                          bgcolor: alpha(theme.colors.text.primary, 0.6),
                          fontSize: 12,
                        }}
                      >
                        {getInitials(fund.name, true)}
                      </Avatar>
                    )}
                    <Typography variant="body2" fontWeight={500}>
                      {fund.name}
                    </Typography>
                  </Stack>
                  <Stack flexDirection="row" alignItems="center">
                    {statusLabel && (
                      <Badge label={statusLabel} color={statusColor} variant="subtle" />
                    )}
                  </Stack>
                </FundRadioItem>
              );
            })
          )}
        </Stack>
      </RadioGroup>

      <FooterButtons>
        <Button color="tertiary" variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button color="primary" variant="solid" disabled={!selectedFund} onClick={onNext}>
          Next
        </Button>
      </FooterButtons>
    </StepContainer>
  );
};
